import { Component, OnInit } from "@angular/core";
import { Location } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import Swal from "sweetalert2";
import { catchError, of } from "rxjs";
import { TipoNovedadService } from "../../../../../shared/services/tipo-novedad.service";

import { Utils } from "../../../../../shared/utils/Utils";
import {
  PesvNovedadesDesplazamientos,
  PesvNovedadesDesplazamientosPayload,
  PesvNovedadesDesplazamientosService,
} from "../../../../../shared/services/PesvNovedadesDesplazamientos.service";
import { PesvDesplazamientosService } from "../../../../../shared/services/pesvDesplazamientos.service";

@Component({
  selector: "app-novedades-desplazamientos",
  templateUrl: "./novedades-desplazamientos.component.html",
  styleUrls: ["./novedades-desplazamientos.component.scss"],
})
export class NovedadesDesplazamientosComponent implements OnInit {
  // ============ TABS PRINCIPALES (formulario / listado) ============
  activeTab = 1;

  // Desplazamiento padre
  idDesplazamiento!: number;
  desplazamiento: any = null;

  get desplazamientoActivo(): boolean {
    return Number(this.desplazamiento?.estado) === 1;
  }

  novedadForm!: FormGroup;
  guardando = false;
  modoEdicion = false;
  idNovedadEditando: number | null = null;

  // Catálogo (tipo es numérico en el backend)
  tiposNovedad: any[] = [];

  // Listado
  novedades: PesvNovedadesDesplazamientos[] = [];
  novedadesFiltradas: PesvNovedadesDesplazamientos[] = [];
  cargandoListado = false;
  filtro = "";
  filtroTipo: number | null = null;
  page = 1;
  pageSize = 10;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private location: Location,
    private novedadesService: PesvNovedadesDesplazamientosService,
    private desplazamientosService: PesvDesplazamientosService,
    private tipoNovedadService: TipoNovedadService,
  ) {}

  private cargarTiposNovedad(): void {
    this.tipoNovedadService.getAll().subscribe({
      next: (data) => {
        this.tiposNovedad = data;
      },
      error: () => {
        this.tiposNovedad = [];
      },
    });
  }

  ngOnInit(): void {
    this.idDesplazamiento = Number(this.route.snapshot.paramMap.get("id"));

    if (!this.idDesplazamiento) {
      Swal.fire("Error", "No se recibió un desplazamiento válido.", "error").then(() =>
        this.volver(),
      );
      return;
    }

    this.inicializarFormulario();
    this.cargarDesplazamiento();
    this.cargarNovedades();
    this.cargarTiposNovedad();
  }

  private inicializarFormulario(): void {
    this.novedadForm = this.fb.group({
      tipo: [null, Validators.required],
      fecha: ["", Validators.required],
      descripcion: ["", Validators.required],
      accion_realizada: [""],
    });
  }

  // ============ Desplazamiento padre ============
  private cargarDesplazamiento(): void {
    this.desplazamientosService
      .getByIdRiesgo(this.idDesplazamiento)
      .pipe(catchError(() => of(null)))
      .subscribe((respuesta: any) => {
        const data = respuesta?.data ?? respuesta;
        this.desplazamiento = Array.isArray(data) ? data[0] ?? null : data ?? null;
        this.activeTab = this.desplazamientoActivo ? 1 : 2;
      });
  }

  private normalizarLista(response: any): PesvNovedadesDesplazamientos[] {
    const lista: PesvNovedadesDesplazamientos[] = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : [];

    return lista.map((novedad) => {
      const estado = novedad.estado;
      const estadoNumerico = Number(estado);

      return {
        ...novedad,
        estado:
          estado === null || estado === undefined || estado === "" || !Number.isFinite(estadoNumerico)
            ? null
            : estadoNumerico,
      };
    });
  }

  // ============ Helpers de presentación ============
  nombreTipo(id?: number | null): string {
    if (id === null || id === undefined) return "-";
    return this.tiposNovedad.find((t) => t.id === Number(id))?.descripcion || "-";
  }

  claseTipo(id?: number | null): string {
    switch (Number(id)) {
      case 2:
        return "bg-danger";
      case 1:
      case 5:
        return "bg-warning text-dark";
      case 3:
      case 4:
      case 6:
        return "bg-info";
      default:
        return "bg-secondary";
    }
  }

  // ============ Listado ============
  cargarNovedades(): void {
    this.cargandoListado = true;
    this.novedadesService.getByDesplazamiento(this.idDesplazamiento).subscribe({
      next: (data: any) => {
        this.novedades = this.normalizarLista(data);
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.novedades = [];
        this.novedadesFiltradas = [];
        this.cargandoListado = false;
      },
    });
  }

  aplicarFiltro(): void {
    const termino = this.filtro.toLowerCase().trim();
    this.novedadesFiltradas = this.novedades.filter((n) => {
      const coincideTexto =
        !termino ||
        [n.descripcion, n.accion_realizada, this.nombreTipo(n.tipo)]
          .filter((v) => v && v !== "-")
          .some((v) => String(v).toLowerCase().includes(termino));

      const coincideTipo = this.filtroTipo === null || Number(n.tipo) === this.filtroTipo;
      return coincideTexto && coincideTipo;
    });
    this.page = 1;
  }

  // ============ Guardar / editar ============
  private aTextoONull(valor: any): string | null {
    if (valor === null || valor === undefined) return null;
    const t = String(valor).trim();
    return t === "" ? null : t;
  }

  private construirPayload(): PesvNovedadesDesplazamientosPayload {
    const raw = this.novedadForm.getRawValue();
    return {
      desplazamiento_id: this.idDesplazamiento,
      tipo: Number(raw.tipo),
      descripcion: String(raw.descripcion).trim(),
      accion_realizada: this.aTextoONull(raw.accion_realizada),
      fecha: raw.fecha,
    };
  }

  guardar(): void {
    if (!this.modoEdicion && !this.desplazamientoActivo) {
      Swal.fire("Desplazamiento inactivo", "No se pueden crear novedades para un desplazamiento inactivo.", "warning");
      return;
    }

    if (this.novedadForm.invalid) {
      this.novedadForm.markAllAsTouched();
      Swal.fire("Campos obligatorios", "Completa todos los campos requeridos.", "warning");
      return;
    }

    const payload = this.construirPayload();
    this.guardando = true;

    const obs =
      this.modoEdicion && this.idNovedadEditando
        ? this.novedadesService.update(this.idNovedadEditando, payload)
        : this.novedadesService.create(payload);

    obs.subscribe({
      next: () => {
        this.guardando = false;
        Swal.fire("Éxito", "Novedad guardada correctamente.", "success");
        this.cancelarEdicion();
        this.cargarNovedades();
        this.activeTab = 2;
      },
      error: (error) => {
        this.guardando = false;
        Swal.fire(
          "Error",
          error?.error?.message || error?.error?.error || "No se pudo guardar la novedad.",
          "error",
        );
      },
    });
  }

  editar(novedad: PesvNovedadesDesplazamientos): void {
    if (Number(novedad.estado) !== 1) {
      Swal.fire("No se puede editar", "Solo se pueden editar novedades activas.", "warning");
      return;
    }

    if (!novedad.id) return;

    this.novedadesService.getById(novedad.id).subscribe({
      next: (respuesta: any) => {
        const detalle = respuesta?.data ?? respuesta;
        this.modoEdicion = true;
        this.idNovedadEditando = detalle.id ?? novedad.id;
        this.novedadForm.patchValue({
          tipo: detalle.tipo ?? null,
          fecha: detalle.fecha?.substring(0, 10),
          descripcion: detalle.descripcion ?? "",
          accion_realizada: detalle.accion_realizada ?? "",
        });
        this.activeTab = 1;
      },
      error: () => Swal.fire("Error", "No se pudo cargar la novedad para editar.", "error"),
    });
  }

  cancelarEdicion(): void {
    this.modoEdicion = false;
    this.idNovedadEditando = null;
    this.novedadForm.reset({ tipo: null, fecha: "", descripcion: "", accion_realizada: "" });
    if (!this.desplazamientoActivo) this.activeTab = 2;
  }

  // ============ Eliminar ============
  eliminar(novedad: PesvNovedadesDesplazamientos): void {
    Swal.fire({
      title: "¿Inactivar novedad?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, inactivar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed || !novedad.id) return;
      this.novedadesService.inactivate(novedad.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Novedad inactivada.", "success");
          if (this.idNovedadEditando === novedad.id) {
            this.cancelarEdicion();
          }
          this.cargarNovedades();
        },
        error: () => Swal.fire("Error", "No se pudo inactivar la novedad.", "error"),
      });
    });
  }

  activar(novedad: PesvNovedadesDesplazamientos): void {
    if (!novedad.id) return;
    this.novedadesService.activate(novedad.id).subscribe({
      next: () => {
        Swal.fire("Éxito", "Novedad activada.", "success");
        this.cargarNovedades();
      },
      error: () => Swal.fire("Error", "No se pudo activar la novedad.", "error"),
    });
  }

  

  // ============ Navegación ============
  volver(): void {
    this.location.back();
  }

  // ============ Exportar ============
  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }
}